'use client';

import React from 'react';
import { useHoloCursor } from '@/lib/use-holo-cursor';

/* ── Types ── */

export interface GitHubRepoWidgetProps {
  /** Full GitHub repository URL (e.g. https://github.com/org/repo) */
  repoUrl: string;
  /** Display name for the repository */
  repoName: string;
  /** Short description of the repository */
  description: string;
  /** Optional Vercel deploy URL — uses Vercel's import flow if omitted */
  vercelDeployUrl?: string;
  /** Last commit message teaser (optional) */
  lastCommitMessage?: string;
  /** Last commit date as a readable string, e.g. "Feb 15, 2026" (optional) */
  lastCommitDate?: string;
}

/* ── SVG Icons ── */

function GitHubIcon() {
  return (
    <svg
      className="grw-icon grw-icon--github"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
    </svg>
  );
}

function MascotBotIcon() {
  return (
    <svg
      className="grw-icon grw-icon--mascotbot"
      viewBox="0 0 38 37"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.78 17.18C.77 19.2.01 21.27.01 22.89c-.09 2.87.55 5.09 2.17 7.1 3.59 4.41 9.14 6.43 16.11 6.63 2.02.05 4.01-.21 5.96-.58 4.73-.91 8.37-2.87 10.82-5.91 2.29-2.83 2.76-5.89 1.96-9.07a22.5 22.5 0 00-1.84-4.49c-1.72-3.46-3.67-6.85-5.75-10.21a69.3 69.3 0 00-3.59-5.15c-.28-.33-.59-.65-.93-.94-.42-.35-.74-.31-1.02.12-.11.17-.22.35-.27.53-.07.25-.14.5-.21.74-.24.86-.48 1.71-.67 2.58-.24 1.09-.38 1.64-.52 2.19-.14.54-.27 1.09-.5 2.14-.13.59-.42 1.2-.7 1.81-.16.33-.31.66-.43.98-.17.43-.51.84-.9 1.2-.59.54-1.6.58-2.28.09a3.23 3.23 0 01-.84-.91 11.1 11.1 0 01-1.03-2.68c-.34-1.47-.6-2.38-.87-3.33-.24-.84-.49-1.71-.8-3.01-.19-.78-.49-1.55-.85-2.3-.27-.56-.71-.58-1.18-.1-.47.48-.91.98-1.3 1.5a52.5 52.5 0 00-4.18 6.74c-1.52 2.86-2.99 5.72-4.42 8.6zm26.03 4.85c0 2.39-1.36 4.32-3.03 4.32s-3.03-1.93-3.03-4.32c0-2.39 1.35-4.32 3.03-4.32s3.03 1.93 3.03 4.32zm-14.81 4.32c1.67 0 3.03-1.93 3.03-4.32s-1.36-4.32-3.03-4.32-3.03 1.93-3.03 4.32 1.36 4.32 3.03 4.32z"
      />
    </svg>
  );
}

function VercelIcon() {
  return (
    <svg
      className="grw-icon grw-icon--vercel"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 1L24 22H0L12 1z" />
    </svg>
  );
}

function GitBranchIcon() {
  return (
    <svg
      className="grw-icon grw-icon--branch"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="6" y1="3" x2="6" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 01-9 9" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      className="grw-icon grw-icon--external"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

/* ── Helpers ── */

/** Extract org/repo from a GitHub URL */
function parseGitHubUrl(url: string): { org: string; repo: string } | null {
  const match = url.match(/github\.com\/([^/]+)\/([^/]+)/);
  if (!match) return null;
  return { org: match[1], repo: match[2].replace(/\.git$/, '') };
}

/** Build Vercel deploy URL from a GitHub repo URL */
function buildVercelDeployUrl(repoUrl: string): string {
  return `https://vercel.com/import/project?template=${encodeURIComponent(repoUrl)}`;
}

/* ── Component ── */

export function GitHubRepoWidget({
  repoUrl,
  repoName,
  description,
  vercelDeployUrl,
  lastCommitMessage,
  lastCommitDate,
}: GitHubRepoWidgetProps) {
  const holo = useHoloCursor();
  const parsed = parseGitHubUrl(repoUrl);
  const orgRepo = parsed ? `${parsed.org}/${parsed.repo}` : repoName;
  const deployUrl = vercelDeployUrl || buildVercelDeployUrl(repoUrl);

  return (
    <div
      ref={holo.ref}
      className="grw"
      onMouseMove={holo.onMouseMove}
      onMouseLeave={holo.onMouseLeave}
    >
      {/* Header: logos row, then repo name below */}
      <div className="grw-header">
        <div className="grw-logos">
          <GitHubIcon />
          <span className="grw-logos-divider" aria-hidden="true">/</span>
          <MascotBotIcon />
        </div>
        <span className="grw-repo-org">{orgRepo}</span>
        <h3 className="grw-repo-name">{repoName}</h3>
      </div>

      {/* Description */}
      <p className="grw-description">{description}</p>

      {/* Last commit info */}
      {(lastCommitMessage || lastCommitDate) && (
        <div className="grw-commit">
          <GitBranchIcon />
          <span className="grw-commit-text">
            {lastCommitMessage && (
              <span className="grw-commit-message">{lastCommitMessage}</span>
            )}
            {lastCommitDate && (
              <span className="grw-commit-date">{lastCommitDate}</span>
            )}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="grw-actions">
        <a
          href={repoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="grw-btn grw-btn--primary"
        >
          <GitHubIcon />
          <span>Clone on GitHub</span>
        </a>
        <a
          href={deployUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="grw-btn grw-btn--secondary"
        >
          <VercelIcon />
          <span>Deploy on Vercel</span>
        </a>
      </div>
    </div>
  );
}
